import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function PlanVsActualOpeLog({ shiftStartDatetime, shiftOutDatetime, machineCode, setSelectedOperationLogs }) {
  const [operationLogs, setOperationLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedOperationRows, setSelectedOperationRows] = useState([]);

  // Fetch operation logs
  useEffect(() => {
    if (shiftStartDatetime && shiftOutDatetime && machineCode) {
      console.log("Sending Request to /operationLogForPlanVsActual:", {
        shiftStartDatetime,
        shiftOutDatetime,
        machineCode,
      });

      setLoading(true);
      setError("");

      axios
        .post(`${API_BASE_URL}/operationlogForPlanVsActual`, {
          shiftStartDatetime: dayjs(shiftStartDatetime).format("YYYY-MM-DD HH:mm:ss"),
          shiftOutDatetime: dayjs(shiftOutDatetime).format("YYYY-MM-DD HH:mm:ss"),
          machineCode,
        })
        .then((response) => {
          console.log("Response from /operationLogForPlanVsActual:", response.data);

          // Set operation logs and auto-select rows with JobStartedTime = null
          const logsWithIds = response.data.map((row, index) => ({
            ...row,
            id: index + 1, // Ensure unique id for each row
          }));
          setOperationLogs(logsWithIds);

          // NEW LOGIC: Auto-select rows where JobStartedTime is null, empty, or undefined
          const selectedRows = logsWithIds.filter((row) => 
            row.JobStartedTime === null || 
            row.JobStartedTime === "" || 
            row.JobStartedTime === undefined
          );
          setSelectedOperationRows(selectedRows);
          setSelectedOperationLogs(selectedRows); // Store selected rows for Step 3
        })
        .catch((err) => {
          console.error("Error fetching operation logs:", err);
          setError("Failed to fetch operation logs. Please try again.");
        })
        .finally(() => setLoading(false));
    } else {
      console.error("Invalid shiftStartDatetime or shiftOutDatetime provided:", {
        shiftStartDatetime,
        shiftOutDatetime,
      });
    }
  }, [shiftStartDatetime, shiftOutDatetime, machineCode, setSelectedOperationLogs]);

  // Handle checkbox selection changes
  const handleCheckboxChange = (rowId, isChecked) => {
    const updatedRows = isChecked
      ? [...selectedOperationRows, operationLogs.find((row) => row.id === rowId)]
      : selectedOperationRows.filter((row) => row.id !== rowId);

    setSelectedOperationRows(updatedRows);
    setSelectedOperationLogs(updatedRows); // Update the parent with the selected rows
  };

  // Helper function to check if JobStartedTime is null/empty
  const isJobStartedTimeNull = (row) => {
    return row.JobStartedTime === null || 
           row.JobStartedTime === "" || 
           row.JobStartedTime === undefined;
  };

  // Define columns for the DataGrid
  const columns = [
    {
      field: "select",
      headerName: "Select",
      width: 100,
      renderCell: (params) =>
        isJobStartedTimeNull(params.row) ? (
          <Checkbox
            checked={selectedOperationRows.some((row) => row.id === params.row.id)}
            onChange={(e) => handleCheckboxChange(params.row.id, e.target.checked)}
          />
        ) : null,
    },
    // { field: "employee_id", headerName: "Employee ID", width: 150 },
    // { field: "name", headerName: "Name", width: 150 },
    // { field: "machine_code", headerName: "Machine Code", width: 150 },
    { field: "job_number", headerName: "Job Number", width: 150 },
    { field: "customer", headerName: "Customer", width: 200 },
    { field: "product", headerName: "Product", width: 100 },
    { field: "main_operation", headerName: "Main Operation", width: 200 },
    { field: "operation_code", headerName: "Operation Code", width: 100 },
    { field: "operation_description", headerName: "Operation Description", width: 200 },
    {
      field: "start_datetime",
      headerName: "Start Datetime",
      width: 200,
      renderCell: (params) => (params.value ? dayjs(params.value).format("YYYY-MM-DD hh:mm A") : "N/A"),
    },
    {
      field: "end_datetime",
      headerName: "End Datetime",
      width: 200,
      renderCell: (params) => (params.value ? dayjs(params.value).format("YYYY-MM-DD hh:mm A") : "N/A"),
    },
    { field: "remarks", headerName: "Remarks", width: 300 },
    { field: "active_status", headerName: "Active Status", width: 100 },
    { field: "operation_type", headerName: "Operation Type", width: 150 },
    { field: "duration_minutes", headerName: "Duration (Minutes)", width: 150 },
    { field: "duration_hours", headerName: "Duration (Hours)", width: 150 },
    { field: "duration", headerName: "Duration", width: 150 },
    {
      field: "JobStartedTime",
      headerName: "Job Started Time",
      width: 200,
      renderCell: (params) => (params.value ? dayjs(params.value).format("YYYY-MM-DD hh:mm A") : "N/A"),
    },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Step 2: Operation-log Filteration
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <DataGrid
          rows={operationLogs}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          getRowClassName={(params) =>
            isJobStartedTimeNull(params.row) ? "highlight-row" : ""
          }
          sx={{
            height: 300, // Set a fixed height to enable vertical scrolling
            // overflowY: "auto", // Ensure vertical scrolling
            "& .MuiDataGrid-viewport": {
              overflowY: "auto", // Apply scrolling to the viewport
            },
            "& .highlight-row": {
              backgroundColor: "#ADD8E6", // Light blue
              "&:hover": {
                backgroundColor: "#87CEEB", // Sky blue on hover
              },
            },
          }}
        />
      )}
    </Box>
  );
}